/**
 * KPICard Component
 * Displays key performance indicators with optional trend and glow effect
 */

import { type ReactNode } from "react";

interface KPICardProps {
  label: string;
  value: string | number;
  trend?: { value: number; direction: "up" | "down" | "neutral" };
  icon?: ReactNode;
  glow?: boolean;
  className?: string;
}

export function KPICard({
  label,
  value,
  trend,
  icon,
  glow = false,
  className = "",
}: KPICardProps) {
  const trendColor =
    trend?.direction === "up"
      ? "text-data-green"
      : trend?.direction === "down"
        ? "text-error-500"
        : "text-secondary-400";
  const trendIcon =
    trend?.direction === "up" ? "+" : trend?.direction === "down" ? "-" : "";

  return (
    <article
      className={`rounded-lg border border-border-default bg-bg-tertiary p-6 ${glow ? "shadow-[0_0_20px_rgba(14,168,157,0.3)]" : ""} ${className}`}
      role="region"
      aria-label={`${label} metric`}
    >
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-medium text-text-secondary">{label}</h3>
        {icon && (
          <div className="text-primary-500" aria-hidden="true">
            {icon}
          </div>
        )}
      </div>
      <div className="flex items-baseline gap-3">
        <span
          className={`text-3xl font-bold text-text-primary ${glow ? "text-primary-400" : ""}`}
          role="status"
          aria-live="polite"
        >
          {value}
        </span>
        {trend && (
          <span
            className={`flex items-center text-sm font-medium ${trendColor}`}
            aria-label={`${trend.direction === "up" ? "Increased" : trend.direction === "down" ? "Decreased" : "No change"} by ${Math.abs(trend.value)}%`}
            role="status"
          >
            <span aria-hidden="true">
              {trendIcon}
              {Math.abs(trend.value)}%
            </span>
          </span>
        )}
      </div>
    </article>
  );
}
